<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/store/modules/auth'
import { useToast } from 'primevue/usetoast'
import OTPInput from '@/components/ui/otp-input/OTPInput.vue'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { Capacitor } from '@capacitor/core'
import { SocialLogin } from '@capgo/capacitor-social-login'
import { IconGoogle, IconApple } from '@/components/icons'
import { useModalStore } from '@/store/modules/modal'
import { useBrandLogo } from '@/composables/useBrandLogo'
import { onMounted } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { formatError } from '@/utils/errorUtils'

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const authStore = useAuthStore()
const toast = useToast()
const recaptcha = useReCaptcha()
const executeRecaptcha = recaptcha?.executeRecaptcha
const route = useRoute()
const modalStore = useModalStore()

const email = ref('')
const code = ref('')
const loading = ref(false)
const step = ref<'identity' | 'verify'>('identity')
const authPurpose = ref<'login' | 'signup'>('login')
const error = ref<string | null>(null)
const isDarkMode = ref(document.documentElement.classList.contains('dark') || window.matchMedia('(prefers-color-scheme: dark)').matches)

const isNative = Capacitor.isNativePlatform()
const oddsLogoIcon = ref('')
const betLogoIcon = ref('')
const betpicLogoIcon = ref('')

onMounted(async () => {
  oddsLogoIcon.value = await useBrandLogo({ context: 'askodds', width: 600, background: 'transparent' })
  betLogoIcon.value = await useBrandLogo({ context: 'askbookmaker', width: 600, background: 'transparent' })
  betpicLogoIcon.value = await useBrandLogo({ context: 'betpic', width: 600, background: 'transparent' })
})

const brandName = computed(() => {
  if (import.meta.env.VITE_SPLASH_MODE === 'odds') return 'Ask Odds'
  if (import.meta.env.VITE_SPLASH_MODE === 'direct') return 'Ask Bookmaker'
  if (import.meta.env.VITE_SPLASH_MODE === 'betpic') return 'BetPic'
  if (route.path.startsWith('/odds')) return 'Ask Odds'
  if (route.path.startsWith('/betpic')) return 'BetPic'
  if (route.path.startsWith('/bet')) return 'Ask Bookmaker'
  return window.location.host.includes('odds') ? 'Ask Odds' : 'Ask Bookmaker'
})

function close() {
  emit('update:visible', false)
  // Reset state after a delay to avoid flicker
  setTimeout(() => {
    step.value = 'identity'
    email.value = ''
    code.value = ''
    error.value = null
    authPurpose.value = 'login'
  }, 300)
}

async function handleAction(purpose: 'login' | 'signup') {
  if (!email.value) return
  loading.value = true
  error.value = null
  authPurpose.value = purpose
  try {
    if (executeRecaptcha) {
      const token = await executeRecaptcha(purpose)
      await authStore.requestCode(email.value, purpose, token)
      step.value = 'verify'
    }
  } catch (e: any) {
    error.value = formatError(e, 'Failed to send code. Please try again.')
  } finally {
    loading.value = false
  }
}

async function handleVerify(codeToVerify?: string) {
  const codeValue = typeof codeToVerify === 'string' ? codeToVerify : code.value
  if (codeValue.length < 6) return
  loading.value = true
  error.value = null
  try {
    if (executeRecaptcha) {
      const token = await executeRecaptcha('verify')
      await authStore.verifyCode(email.value, codeValue, authPurpose.value, token)
      toast.add({ severity: 'success', summary: 'Welcome!', life: 3000 })
      close()
    }
  } catch (e: any) {
    error.value = 'Invalid or expired code'
    code.value = ''
  } finally {
    loading.value = false
  }
}

async function handleGoogleLogin() {
  loading.value = true
  error.value = null
  
  let messageHandler: ((event: MessageEvent) => void) | null = null

  try {
    await authStore.clearSocialSessions()
    const googleClientId = window.__APP_CONFIG__?.googleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID
    
    // Create a promise that resolves when the postMessage is received
    const postMessagePromise = new Promise<{ idToken: string; accessToken: string }>((resolve, reject) => {
      messageHandler = (event: MessageEvent) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
          if (data.provider === 'google') {
            if (data.success && data.result) {
              resolve(data.result)
            } else {
              reject(new Error(data.error || 'Google login failed'))
            }
          }
        } catch (e) {
          // Ignore non-JSON or unrelated messages
        }
      }
      window.addEventListener('message', messageHandler)
    })

    const loginPromise = SocialLogin.login({
      provider: 'google',
      options: { 
        scopes: ['email', 'profile'],
        clientId: googleClientId,
        prompt: 'select_account'
      }
    })

    // Wait for either the plugin to resolve (native) or our postMessage (web)
    const result = await Promise.race([
      loginPromise.then(res => {
        if (res.result && res.result.responseType === 'online') {
          return { idToken: res.result.idToken || '', accessToken: res.result.accessToken?.token || '' }
        }
        throw new Error('Invalid login response')
      }),
      postMessagePromise
    ])

    const idToken = result.idToken
    if (!idToken) {
      toast.add({ severity: 'error', summary: 'Google login error', detail: 'No idToken received', life: 5000 })
      throw new Error('No idToken received')
    }
    
    console.log('Got Google token:', idToken.substring(0, 10) + '...')
    let recaptchaToken: string | undefined
    if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('google_login')
    }
    
    try {
      await authStore.loginWithGoogle(idToken, recaptchaToken)
      toast.add({ severity: 'success', summary: 'Google login successful', life: 5000 })
      close()
    } catch (e: any) {
        const status = e.response?.status
        const data = e.response?.data
        
        const isNotFound = status === 404 || 
                          data?.message?.includes('not linked') || 
                          (typeof data === 'string' && data.includes('not linked'))
        
        if (isNotFound) {
          // Close AuthModal first to avoid stacking issues
          close()
          setTimeout(() => {
            modalStore.open({
              title: 'Create New Account?',
              message: "We couldn't find an account linked to this Google ID. Would you like to create a new one using these credentials?",
              confirmText: 'Sign Up',
              onConfirm: async () => {
                loading.value = true
                try {
                  let signupToken: string | undefined
                  if (executeRecaptcha) {
                    signupToken = await executeRecaptcha('google_signup')
                  }
                  await authStore.signupGoogle(idToken, signupToken)
                  toast.add({ severity: 'success', summary: 'Google registration successful', life: 5000 })
                } catch (err: any) {
                  const msg = formatError(err, 'Registration failed')
                  toast.add({ severity: 'error', summary: 'Registration failed', detail: msg, life: 5000 })
                } finally {
                  loading.value = false
                }
              }
            })
          }, 100)
        } else {
          error.value = formatError(e, 'Google login failed')
        }
      }
  } catch (err: any) {
    console.error('Login error:', err)
    error.value = formatError(err, 'Authentication aborted')
    await authStore.clearSocialSessions()
  } finally {
    loading.value = false
    if (messageHandler) {
      window.removeEventListener('message', messageHandler)
    }
  }
}

async function handleAppleLogin() {
  loading.value = true
  error.value = null
  try {
    await authStore.clearSocialSessions()
    const result = await SocialLogin.login({
      provider: 'apple',
      options: { 
        scopes: ['email', 'name'],
      }
    })

    if (result.result) {
      const idToken = result.result.idToken
      if (!idToken) {
        toast.add({ severity: 'error', summary: 'Apple login error', detail: 'No idToken received', life: 5000 })
        throw new Error('No idToken received')
      }
      
      let token: string | undefined
      if (executeRecaptcha) {
        token = await executeRecaptcha('apple_login')
      }
      
      try {
        await authStore.loginWithApple(idToken, token)
        toast.add({ severity: 'success', summary: 'Apple login successful', life: 5000 })
        close()
      } catch (e: any) {
        const status = e.response?.status
        const data = e.response?.data
        
        const isNotFound = status === 404 || 
                          data?.message?.includes('not linked') || 
                          (typeof data === 'string' && data.includes('not linked'))
        
        if (isNotFound) {
          // Close AuthModal first to avoid stacking issues
          close()
          setTimeout(() => {
            modalStore.open({
              title: 'Create New Account?',
              message: "We couldn't find an account linked to this Apple ID. Would you like to create a new one using these credentials?",
              confirmText: 'Sign Up',
              onConfirm: async () => {
                loading.value = true
                try {
                  let signupToken: string | undefined
                  if (executeRecaptcha) {
                    signupToken = await executeRecaptcha('apple_signup')
                  }
                  await authStore.signupApple(idToken, signupToken)
                  toast.add({ severity: 'success', summary: 'Apple registration successful', life: 5000 })
                } catch (err: any) {
                  const msg = formatError(err, 'Registration failed')
                  toast.add({ severity: 'error', summary: 'Registration failed', detail: msg, life: 5000 })
                } finally {
                  loading.value = false
                }
              }
            })
          }, 100)
        } else {
          error.value = formatError(e, 'Apple login failed')
          toast.add({ severity: 'error', summary: 'Login Error', detail: error.value, life: 5000 })
        }
      }
    } else {
      toast.add({ severity: 'warn', summary: 'No auth data', detail: 'Received empty result from social login', life: 5000 })
    }
  } catch (e: any) {
    error.value = formatError(e, 'Apple login failed')
    toast.add({ severity: 'error', summary: 'System Error', detail: error.value, life: 5000 })
    await authStore.clearSocialSessions()
  } finally {
    loading.value = false
  }
}

// Removed onGoogleResponse as we now use SocialLogin for all platforms
</script>

<template>
  <Dialog :open="props.visible" @update:open="emit('update:visible', $event)">
    <DialogContent 
      @open-auto-focus.prevent
      class="max-w-md w-full overflow-hidden p-0 rounded-2xl border-none shadow-2xl bg-white dark:bg-gray-900 max-md:h-screen max-md:max-h-screen max-md:rounded-none [&>button]:hidden flex flex-col gap-0"
      :style="{ zIndex: 2000 }"
    >
      <!-- Top Decorative Branding -->
      <div class="h-32 bg-gradient-to-br from-primary to-accent flex items-center justify-center relative overflow-hidden flex-shrink-0">
        <div class="absolute inset-0 opacity-20 pattern-bg"></div>
        <div class="z-10 text-white text-center px-6 w-full">
            <img v-if="brandName == 'Ask Odds'" :src="oddsLogoIcon" class="h-full w-auto mx-auto object-contain" alt="logo"/>
            <img v-else-if="brandName == 'Ask Bookmaker'" :src="betLogoIcon" class="h-full w-auto mx-auto object-contain" alt="logo"/>
            <img v-else-if="brandName == 'BetPic'" :src="betpicLogoIcon" class="h-full w-auto mx-auto object-contain" alt="logo"/>
            <template v-else>
              <h2 class="text-3xl font-bold tracking-tight">Login</h2>
            </template>
        </div>
      </div>

      <div class="p-6 flex-1 overflow-y-auto">
        <Transition name="fade-slide" mode="out-in">
          <!-- PROCESSING STATE -->
          <div v-if="loading" key="loading" class="flex flex-col items-center justify-center py-12 space-y-6">
            <div class="h-20 w-20 rounded-full border-4 border-primary/10 border-t-primary animate-spin"></div>
            <div class="text-center space-y-2">
              <h3 class="text-lg font-bold text-gray-900 dark:text-white">Authenticating</h3>
              <p class="text-sm text-gray-500 max-w-[200px]">Please wait while we securely process your request...</p>
            </div>
          </div>

          <!-- STEP 1: IDENTITY (Email Input & Purpose Selection) -->
          <form v-else-if="step === 'identity'" key="identity" class="space-y-6" @submit.prevent="handleAction('login')">
            <div class="text-center">
              <h3 class="text-xl font-semibold mb-1">Welcome back</h3>
              <p class="text-sm text-gray-500">Enter your email to get started</p>
            </div>
            
            <div class="space-y-4">
              <div class="space-y-2">
                <Input 
                  v-model="email" 
                  type="email" 
                  name="email"
                  inputmode="email"
                  autocomplete="email"
                  autocapitalize="none"
                  spellcheck="false"
                  placeholder="name@example.com" 
                  class="h-12 text-base md:text-lg rounded-xl transition-all focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div class="grid grid-cols-2 gap-3">
                <Button 
                  type="submit"
                  class="h-12 rounded-xl shadow-lg shadow-primary/10 transition-transform active:scale-[0.98]" 
                  :disabled="!email"
                >
                  Login
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  class="h-12 rounded-xl border-gray-200 dark:border-gray-800 transition-transform active:scale-[0.98]" 
                  @click="handleAction('signup')"
                  :disabled="!email"
                >
                  Register
                </Button>
              </div>

              <div class="relative py-2">
                <div class="absolute inset-0 flex items-center"><div class="w-full border-t border-border"></div></div>
                <div class="relative flex justify-center text-xs uppercase"><span class="bg-white dark:bg-gray-900 px-2 text-gray-400 font-medium">Or continue with</span></div>
              </div>

              <div class="flex flex-col items-center gap-3">
                <div class="w-full space-y-3">
                  <Button type="button" variant="outline" class="w-full h-12 rounded-xl border-gray-200 dark:border-gray-800 justify-start px-6 gap-3" @click="handleGoogleLogin">
                    <IconGoogle class="w-5 h-5 flex-shrink-0" /> <span class="flex-1 text-center pr-5">Continue with Google</span>
                  </Button>
                  <Button type="button" variant="outline" class="w-full h-12 rounded-xl border-gray-200 dark:border-gray-800 justify-start px-6 gap-3" @click="handleAppleLogin">
                    <IconApple class="w-5 h-5 flex-shrink-0" /> <span class="flex-1 text-center pr-5">Continue with Apple</span>
                  </Button>
                </div>
              </div>

              <div v-if="error" class="text-center text-sm text-red-500 font-medium p-2 bg-red-50 dark:bg-red-900/10 rounded-lg whitespace-pre-line">
                {{ error }}
              </div>
            </div>
          </form>

          <!-- STEP 2: VERIFY -->
          <div v-else-if="step === 'verify'" key="verify" class="space-y-8 py-4">
             <div class="text-center">
                <h3 class="text-2xl font-bold mb-2">Check your email</h3>
                <p class="text-sm text-gray-500 mb-6">
                  We've sent a 6-digit code to <br/><strong>{{ email }}</strong>
                </p>
             </div>

             <OTPInput v-model="code" @complete="handleVerify" :disabled="loading" />

             <div v-if="error" class="text-center text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1 whitespace-pre-line">
                {{ error }}
             </div>

             <div class="text-center space-y-4 mt-8">
               <p class="text-xs text-gray-400">Didn't receive code?</p>
               <Button variant="outline" size="sm" class="rounded-xl border-gray-200 dark:border-gray-800" @click="handleAction(authPurpose)">
                 Resend Code
               </Button>
               <div class="pt-2">
                 <Button variant="ghost" size="sm" @click="step = 'identity'" class="text-gray-400">
                   Back to start
                 </Button>
               </div>
             </div>
          </div>
        </Transition>
      </div>

      <div class="p-0 border-t border-border flex justify-center bg-gray-50/50 dark:bg-black/20">
         <Button variant="ghost" class="w-full !h-16 text-gray-500 dark:text-gray-400 font-medium hover:bg-transparent rounded-none" @click="close">
           Cancel
         </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
.pattern-bg {
  background-image: radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0);
  background-size: 20px 20px;
}

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from { opacity: 0; transform: translateX(20px); }
.fade-slide-leave-to { opacity: 0; transform: translateX(-20px); }
</style>
